import React, { useEffect, useState } from "react";
import { db } from "../../firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import "./AdminDashboard.css";
import AdminNavBar from "./AdminNavBar";

const AdminDashboard = () => {
  const [products, setProducts] = useState(null);      // REAL products
  const [analytics, setAnalytics] = useState(null);    // analyticsProducts (all-time)
  const [dailyStats, setDailyStats] = useState(null);  // analyticsDaily (daily totals)
  const [activity, setActivity] = useState(null);      // analyticsActivity (per event)

  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dateRange, setDateRange] = useState("7");

  const [sortField, setSortField] = useState("views");
  const [sortDirection, setSortDirection] = useState("desc");

  // -----------------------------
  // LOAD REAL PRODUCTS
  // -----------------------------
  useEffect(() => {
    async function loadProducts() {
      try {
        const snap = await getDocs(collection(db, "products"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        setProducts(list);
      } catch (err) {
        console.error("❌ Error loading REAL products:", err);
        setProducts([]);
      }
    }

    loadProducts();
  }, []);

  // -----------------------------
  // LOAD ANALYTICS PRODUCTS (ALL-TIME)
  // -----------------------------
  useEffect(() => {
    async function loadAnalytics() {
      try {
        const snap = await getDocs(collection(db, "analyticsProducts"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        setAnalytics(list);
      } catch (err) {
        console.error("❌ Error loading analyticsProducts:", err);
        setAnalytics([]);
      }
    }

    loadAnalytics();
  }, []);

  // -----------------------------
  // LOAD DAILY STATS
  // -----------------------------
  useEffect(() => {
    async function loadDaily() {
      try {
        const snap = await getDocs(collection(db, "analyticsDaily"));
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((d) => typeof d.date === "string")
          .sort((a, b) => (a.date < b.date ? 1 : -1));

        setDailyStats(list);
      } catch (err) {
        console.error("❌ Error loading analyticsDaily:", err);
        setDailyStats([]);
      }
    }

    loadDaily();
  }, []);

  // -----------------------------
  // LOAD ACTIVITY (PER EVENT)
  // -----------------------------
  useEffect(() => {
    async function loadActivity() {
      try {
        const snap = await getDocs(collection(db, "analyticsActivity"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        setActivity(list);
      } catch (err) {
        console.error("❌ Error loading analyticsActivity:", err);
        setActivity([]);
      }
    }

    loadActivity();
  }, []);

  if (!products || !analytics || !dailyStats || !activity) {
    return <div className="loading">Loading dashboard...</div>;
  }

  // -----------------------------
  // DATE RANGE CALC (DAILY TOTALS)
  // -----------------------------
  const daily = dailyStats.map((d) => ({
    ...d,
    dateObj: new Date(d.date + "T00:00:00"),
  }));

  const now = new Date();
  const days = parseInt(dateRange, 10);

  const filteredDaily = daily.filter((d) => {
    const diff = (now - d.dateObj) / (1000 * 60 * 60 * 24);
    return diff <= days - 1;
  });

  const totalViewsRange = filteredDaily.reduce(
    (sum, d) => sum + (d.totalViews || 0),
    0
  );
  const totalClicksRange = filteredDaily.reduce(
    (sum, d) => sum + (d.totalClicks || 0),
    0
  );

  // -----------------------------
  // PER-PRODUCT STATS FOR SELECTED DATE RANGE (FROM ACTIVITY)
  // -----------------------------
  const filteredActivity = activity.filter((a) => {
    const ts = a.timestamp?.seconds
      ? new Date(a.timestamp.seconds * 1000)
      : null;
    if (!ts) return false;

    const diff = (now - ts) / (1000 * 60 * 60 * 24);
    return diff <= days - 1;
  });

  const rangeStats = {};
  filteredActivity.forEach((a) => {
    if (!a.productId) return;
    if (!rangeStats[a.productId]) {
      rangeStats[a.productId] = { views: 0, clicks: 0 };
    }
    if (a.type === "view") rangeStats[a.productId].views++;
    if (a.type === "click") rangeStats[a.productId].clicks++;
  });

  // -----------------------------
  // MERGE REAL PRODUCTS + ANALYTICS (ALL-TIME + RANGE)
  // -----------------------------
  const merged = products.map((p) => {
    const allTime = analytics.find((a) => a.id === p.id) || {};
    const range = rangeStats[p.id] || { views: 0, clicks: 0 };

    return {
      ...p,
      // range-based (for current filter)
      views: range.views,
      amazonClicks: range.clicks,
      // all-time (if you ever want to show it)
      viewsAllTime: allTime.views || 0,
      clicksAllTime: allTime.amazonClicks || 0,
    };
  });

  // -----------------------------
  // CATEGORY FILTER
  // -----------------------------
  const filtered = merged.filter((p) => {
    if (categoryFilter === "all") return true;
    return p.category === categoryFilter;
  });

  // -----------------------------
  // CTR CALCULATION (RANGE-BASED)
  // -----------------------------
  const withCTR = filtered.map((p) => ({
    ...p,
    ctr: p.views ? p.amazonClicks / p.views : 0,
  }));

  // -----------------------------
  // SORT HANDLER
  // -----------------------------
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // -----------------------------
  // APPLY SORTING (RANGE-BASED)
  // -----------------------------
  const sortedProducts = [...withCTR].sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];

    if (sortField === "title") {
      valA = valA?.toLowerCase() || "";
      valB = valB?.toLowerCase() || "";
    }

    if (valA < valB) return sortDirection === "asc" ? -1 : 1;
    if (valA > valB) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <div className="admin-dashboard">

      <AdminNavBar/>

      {/* Summary Cards */}
      <section className="admin-summary">
        <div className="summary-card">
          <h3>Total Products</h3>
          <p>{merged.length}</p>
        </div>

        <div className="summary-card">
          <h3>Views ({dateRange === "1" ? "Today" : `Last ${dateRange} Days`})</h3>
          <p>{totalViewsRange}</p>
        </div>

        <div className="summary-card">
          <h3>Amazon Clicks ({dateRange === "1" ? "Today" : `Last ${dateRange} Days`})</h3>
          <p>{totalClicksRange}</p>
        </div>
      </section>

      {/* Filters */}
      <section className="admin-filters">
        <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
          <option value="1">Today</option>
          <option value="7">Last 7 Days</option>
          <option value="30">Last 30 Days</option>
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="all">All Categories</option>
          <option value="coloring-books">Coloring Books</option>
          <option value="journals">Journals</option>
        </select>
      </section>

      {/* Top Products (range-based) */}
      <section className="admin-top-products">
        <h2>
          Top Products ({dateRange === "1" ? "Today" : `Last ${dateRange} Days`})
        </h2>

        <table>
          <thead>
            <tr>
              <th>#</th>

              <th onClick={() => handleSort("title")} className="sortable">
                Product {sortField === "title" && (sortDirection === "asc" ? "▲" : "▼")}
              </th>

              <th onClick={() => handleSort("views")} className="sortable">
                Views {sortField === "views" && (sortDirection === "asc" ? "▲" : "▼")}
              </th>

              <th onClick={() => handleSort("amazonClicks")} className="sortable">
                Clicks {sortField === "amazonClicks" && (sortDirection === "asc" ? "▲" : "▼")}
              </th>

              <th onClick={() => handleSort("ctr")} className="sortable">
                CTR {sortField === "ctr" && (sortDirection === "asc" ? "▲" : "▼")}
              </th>
            </tr>
          </thead>

          <tbody>
            {sortedProducts.map((p, index) => (
              <tr key={p.id}>
                <td>{index + 1}</td>

                <td>
                  <a href={`/shop/${p.category}/${p.id}`} className="product-link">
                    {p.title}
                  </a>
                </td>

                <td>{p.views}</td>
                <td>{p.amazonClicks}</td>
                <td>{(p.ctr * 100).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default AdminDashboard;
